import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconFileText, IconUpload, IconX } from "@tabler/icons-react";
import s from "../partnerPortal.module.css";
import { Panel, SectionTitle, Modal, ModalClose } from "./shared";
import { AV_GRAD, fitColor, initialsOf } from "../portalData";
import {
  listJobSpecs,
  createJobSpec,
  updateJobSpec,
  deleteJobSpec,
  listFitPool,
  uploadFitCandidates,
  uploadFitCvs,
  removeFitCandidate,
  toggleFitShortlist,
  exportFitShortlist,
  sendFitAssessments,
  partnerGetTests,
  rolesFromTestsResponse,
} from "../../../services/partnerCalls";
import { errorHandler } from "../../../utils";
import { DEFAULT_ERROR_MESSAGE } from "../../../constants";
import { Spinner } from "../../../components/UI/Spinner/Spinner";
import PartnerRoleOptions from "../PartnerRoleOptions";
import { PARTNER_ROLE_CATALOG, mergePartnerRoleCatalog, displayRole } from "../partnerRoleCatalog";
import { parseCandidateList } from "../candidateListParser";
import { assessDescription, specQuality, TITLE_ONLY_CAP } from "../specQuality";

const ROW_COLS = "26px 34px 1fr 140px 46px 30px 88px";
const specKey = (x) => String(x?._id || x?.id || "");
const errMsg = (error) => error?.response?.data?.message || DEFAULT_ERROR_MESSAGE;

// CV upload limits — mirror the API (awasource-public-api candidateFit.routes)
const CV_EXTENSIONS = [".pdf", ".docx"];
const MAX_CV_BYTES = 5 * 1024 * 1024;
// Files go up in several requests: at most CV_BATCH files and CV_BATCH_BYTES
// each, so no single request is too big for a proxy in front of the API.
const CV_BATCH = 20; // the API takes up to 50
const CV_BATCH_BYTES = 8 * 1024 * 1024;
const MAX_CVS = 500;
const POLL_MS = 3000;

const extOf = (name) => {
  const i = String(name || "").lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
};
const cvProblem = (file) => {
  const ext = extOf(file.name);
  if (ext === ".doc") return "Old Word (.doc) — save as .docx or PDF";
  if (!CV_EXTENSIONS.includes(ext)) return "Not a PDF or DOCX";
  if (file.size > MAX_CV_BYTES) return "Larger than 5MB";
  if (!file.size) return "Empty file";
  return null;
};
/** Groups files into requests of ≤ CV_BATCH files and ≤ CV_BATCH_BYTES (a lone larger file goes alone). */
const batchFiles = (files) => {
  const batches = [];
  let cur = [];
  let bytes = 0;
  for (const f of files) {
    if (cur.length && (cur.length >= CV_BATCH || bytes + f.size > CV_BATCH_BYTES)) {
      batches.push(cur);
      cur = [];
      bytes = 0;
    }
    cur.push(f);
    bytes += f.size;
  }
  if (cur.length) batches.push(cur);
  return batches;
};
const fmtSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`;

const TEST_TAGS = {
  assessed: { label: "Tested", tone: "teal" },
  completed: { label: "Test completed", tone: "teal" },
  sent: { label: "Test sent", tone: "amber" },
  expired: { label: "Test expired", tone: "muted" },
  cv_only: { label: "Not tested", tone: "muted" },
};
const TONES = {
  teal: { color: "#2dd4bf", background: "rgba(45,212,191,0.1)", border: "rgba(45,212,191,0.3)" },
  amber: { color: "#fbbf24", background: "rgba(251,191,36,0.08)", border: "rgba(251,191,36,0.3)" },
  muted: { color: "#8898b5", background: "#0a1120", border: "#16223a" },
};
const Tag = ({ tone = "muted", children, title }) => {
  const t = TONES[tone];
  return (
    <span
      title={title}
      style={{
        fontSize: 8.5,
        fontWeight: 700,
        color: t.color,
        background: t.background,
        border: `1px solid ${t.border}`,
        borderRadius: 4,
        padding: "1px 6px",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
};
const Chip = ({ children, muted }) => (
  <span
    style={{
      display: "inline-block",
      fontSize: 11,
      padding: "3px 9px",
      borderRadius: 20,
      margin: "0 6px 6px 0",
      color: muted ? "#8898b5" : "#2dd4bf",
      background: muted ? "#0a1120" : "rgba(45,212,191,0.08)",
      border: `1px solid ${muted ? "#16223a" : "rgba(45,212,191,0.3)"}`,
    }}
  >
    {children}
  </span>
);

const isScored = (c) => c.scoring_status === "scored" && c.fit_score !== null && c.fit_score !== undefined;

export default function CandidateFit() {
  const navigate = useNavigate();
  const [specs, setSpecs] = useState([]);
  const [specId, setSpecId] = useState("");
  const [specModal, setSpecModal] = useState(null);
  const [roles, setRoles] = useState(PARTNER_ROLE_CATALOG);
  const [sortBy, setSortBy] = useState("fit");
  const [detailId, setDetailId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [pool, setPool] = useState([]);
  const [tab, setTab] = useState("pool");
  const [uploadModal, setUploadModal] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [sendFlash, setSendFlash] = useState(null);
  const [infoFlash, setInfoFlash] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const pollRef = useRef(null);
  const currentSpecRef = useRef("");

  const spec = specs.find((x) => specKey(x) === specId) || null;
  const quality = specQuality(spec);
  const detail = detailId ? pool.find((c) => c.id === detailId) || null : null;

  const fetchRoles = async () => {
    try {
      const res = await partnerGetTests();
      setRoles(mergePartnerRoleCatalog(rolesFromTestsResponse(res)));
    } catch (error) {
      errorHandler(errMsg(error));
    }
  };

  const fetchSpecs = async (selectId) => {
    try {
      const res = await listJobSpecs();
      const list = res || [];
      setSpecs(list);
      if (selectId) setSpecId(String(selectId));
      else if (!list.some((x) => specKey(x) === specId)) setSpecId(list.length ? specKey(list[0]) : "");
      return list;
    } catch (error) {
      errorHandler(errMsg(error));
      return [];
    }
  };

  // quiet = a background refresh (polling, after an action): no spinner
  const fetchPool = async (id, sort, { quiet = false } = {}) => {
    if (!id) {
      setPool([]);
      return;
    }
    if (!quiet) {
      setLoading(true);
      setLoadError("");
    }
    try {
      const res = await listFitPool(id, sort);
      if (currentSpecRef.current === id) setPool(res || []);
    } catch (error) {
      if (!quiet) {
        setPool([]);
        setLoadError(errMsg(error));
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchSpecs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    currentSpecRef.current = specId;
    setDetailId(null);
    fetchPool(specId, sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specId, sortBy]);

  // CVs are scored in the background after upload (and the whole pool after
  // a spec edit): keep refreshing while any row is still "Scoring…".
  const pendingCount = pool.filter((c) => c.scoring_status === "pending").length;
  useEffect(() => {
    clearTimeout(pollRef.current);
    if (pendingCount && specId) {
      pollRef.current = setTimeout(() => fetchPool(specId, sortBy, { quiet: true }), POLL_MS);
    }
    return () => clearTimeout(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool, specId, sortBy]);

  const ranked = pool;
  const scoredRows = ranked.filter(isScored);
  const avg = scoredRows.length
    ? Math.round(scoredRows.reduce((sum, c) => sum + c.fit_score, 0) / scoredRows.length)
    : 0;
  const shortRanked = ranked.filter((c) => c.shortlisted);
  const shortCount = shortRanked.length;
  const shortWithEmail = shortRanked.filter((c) => c.email).length;
  const rows = tab === "shortlist" ? shortRanked : ranked;

  const toggleShort = async (candidateId) => {
    try {
      const updated = await toggleFitShortlist(candidateId);
      setPool((prev) => prev.map((c) => (c.id === candidateId ? { ...c, ...updated } : c)));
    } catch (error) {
      errorHandler(errMsg(error));
    }
  };

  const removeCandidate = async (candidateId) => {
    if (busy) return;
    setBusy(true);
    try {
      await removeFitCandidate(candidateId);
      setPool((prev) => prev.filter((c) => c.id !== candidateId));
      setDetailId(null);
    } catch (error) {
      errorHandler(errMsg(error));
    } finally {
      setBusy(false);
      setConfirmRemove(false);
    }
  };

  // ─── Job specs ───────────────────────────────────────────────────────────

  const openNew = () =>
    setSpecModal({ mode: "new", id: null, title: "", role: roles[0]?.t || "", description: "", confirmDelete: false });
  const openEdit = () =>
    spec &&
    setSpecModal({
      mode: "edit",
      id: specKey(spec),
      title: spec.title,
      role: displayRole(spec.role),
      description: spec.description || "",
      confirmDelete: false,
    });

  const saveSpec = async () => {
    const m = specModal;
    if (!m || !m.title.trim() || busy) return;
    setBusy(true);
    try {
      if (m.mode === "new") {
        const created = await createJobSpec({ title: m.title.trim(), role: displayRole(m.role), description: m.description });
        await fetchSpecs(specKey(created));
      } else {
        const updated = await updateJobSpec(m.id, { title: m.title.trim(), role: displayRole(m.role), description: m.description });
        await fetchSpecs(m.id);
        if (updated?.rescoring) {
          setInfoFlash(
            `Spec updated — re-scoring ${updated.rescoring} candidate${updated.rescoring === 1 ? "" : "s"} against it. Scores update here as they finish.`,
          );
          await fetchPool(m.id, sortBy, { quiet: true });
        }
      }
      setSpecModal(null);
    } catch (error) {
      errorHandler(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  const removeSpec = async () => {
    const m = specModal;
    if (!m?.id || busy) return;
    if (!m.confirmDelete) {
      setSpecModal({ ...m, confirmDelete: true });
      return;
    }
    setBusy(true);
    try {
      await deleteJobSpec(m.id);
      const list = await fetchSpecs();
      setSpecId(list.length ? specKey(list.find((x) => specKey(x) !== m.id) || list[0]) : "");
      setSpecModal(null);
      setInfoFlash(`Deleted the “${m.title}” spec.`);
    } catch (error) {
      errorHandler(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  // ─── Sending tests ───────────────────────────────────────────────────────

  const sendTests = async (cands) => {
    if (!cands.length || busy) return;
    setBusy(true);
    try {
      const data = (await sendFitAssessments(cands.map((c) => c.id), displayRole(spec?.role))) || {};
      setSendFlash({ n: (data.queued || []).length, failed: data.failed || [] });
      await fetchPool(specId, sortBy, { quiet: true });
    } catch (error) {
      errorHandler(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  const exportShortlist = async () => {
    if (!shortRanked.length || !specId) return;
    try {
      const blob = await exportFitShortlist(specId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shortlist-${(spec?.title || "candidates").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      errorHandler(errMsg(error));
    }
  };

  // ─── Upload: CV files, or a candidate list ───────────────────────────────

  const openUpload = () =>
    setUploadModal({ mode: "cv", files: [], text: "", progress: null, result: null, uploading: false });

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setUploadModal((m) => {
      if (!m) return m;
      const known = new Set(m.files.map((f) => `${f.file.name}:${f.file.size}`));
      const added = incoming
        .filter((file) => !known.has(`${file.name}:${file.size}`))
        .map((file) => ({ file, problem: cvProblem(file) }));
      const files = [...m.files, ...added].slice(0, MAX_CVS);
      return { ...m, files, result: null };
    });
  };

  const readListFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setUploadModal((m) => (m ? { ...m, text: String(reader.result || "") } : m));
    reader.onerror = () => errorHandler("Could not read that file. Use a CSV or text list.");
    reader.readAsText(file);
  };

  const uploadCvs = async () => {
    const m = uploadModal;
    const good = m.files.filter((f) => !f.problem).map((f) => f.file);
    if (!good.length || !specId || m.uploading) return;
    setUploadModal({ ...m, uploading: true, progress: { done: 0, total: good.length } });

    let added = 0;
    const failed = m.files.filter((f) => f.problem).map((f) => ({ file: f.file.name, reason: f.problem }));
    let done = 0;
    for (const batch of batchFiles(good)) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await uploadFitCvs(specId, batch);
        added += (res?.candidates || []).length;
        failed.push(...(res?.failed || []));
      } catch (error) {
        const reason =
          error?.response?.status === 413
            ? "The upload was too large for the server — try fewer or smaller files"
            : errMsg(error);
        batch.forEach((f) => failed.push({ file: f.name, reason }));
      }
      done += batch.length;
      const progress = { done, total: good.length };
      setUploadModal((cur) => (cur ? { ...cur, progress } : cur));
    }

    setUploadModal((cur) => (cur ? { ...cur, uploading: false, files: [], result: { added, failed } } : cur));
    setTab("pool");
    fetchPool(specId, sortBy, { quiet: true });
  };

  const listParse = uploadModal?.mode === "list" ? parseCandidateList(uploadModal.text) : null;

  const importList = async () => {
    const items = listParse?.candidates || [];
    if (!items.length || !specId || busy) return;
    setBusy(true);
    try {
      await uploadFitCandidates(specId, items);
      setUploadModal(null);
      setTab("pool");
      await fetchPool(specId, sortBy);
    } catch (error) {
      errorHandler(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  const chip = (id, label) => (
    <button
      key={id}
      type="button"
      onClick={() => setSortBy(id)}
      style={{
        padding: "6px 12px",
        borderRadius: 20,
        border: `1px solid ${sortBy === id ? "transparent" : "#24344e"}`,
        cursor: "pointer",
        fontSize: 11,
        fontWeight: 700,
        fontFamily: "inherit",
        background: sortBy === id ? "#1a2540" : "transparent",
        color: sortBy === id ? "#e4ecf7" : "#8898b5",
      }}
    >
      {label}
    </button>
  );

  const metaLine = (c) =>
    [
      c.email || null,
      c.years_experience ? `${c.years_experience} yrs` : null,
      c.qualification,
      c.source === "cv" ? c.cv_filename : null,
    ]
      .filter(Boolean)
      .join(" · ");

  const goodFiles = uploadModal?.files?.filter((f) => !f.problem) || [];
  const badFiles = uploadModal?.files?.filter((f) => f.problem) || [];

  return (
    <div>
      <SectionTitle
        title="Candidate Fit"
        sub="Upload CVs (PDF or Word) and rank them against your job spec — CV & skills and experience matched to the spec. Psychometric test results stay separate, in Test Sessions."
      />

      <Panel className={s.pg66}>
        <div className={s.pg98}>
          <div className={s.pg99}>
            <div>
              <label className={s.label}>Score pool against · your job spec</label>
              <select
                className={`${s.select} ${s.pg100}`}
                value={specId}
                onChange={(e) => setSpecId(e.target.value)}
                disabled={!specs.length}
              >
                {specs.length === 0 && <option value="">No job specs yet</option>}
                {specs.map((x) => (
                  <option key={specKey(x)} value={specKey(x)}>
                    {x.title}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" className={`${s.btnGhost} ${s.fitToolbarBtn}`} onClick={openNew}>
              + New spec
            </button>
            <button
              type="button"
              className={`${s.btnSolid} ${s.fitToolbarBtn}`}
              disabled={!specId}
              title={specId ? "Upload CV files (PDF or DOCX), or import a list" : "Create a job spec first"}
              onClick={openUpload}
            >
              <IconUpload size={14} />
              Upload CVs
            </button>
          </div>

          <div className={s.pg82}>
            {[
              ["Candidates", ranked.length],
              ["Avg fit", scoredRows.length ? `${avg}%` : "—"],
              ["Shortlisted", shortCount],
            ].map(([k, v]) => (
              <div key={k} className={`${s.statCell} ${s.pg101}`}>
                <div className={s.pg102}>{v}</div>
                <div className={`${s.statCellLabel} ${s.pg103}`}>{k}</div>
              </div>
            ))}
          </div>
        </div>
      </Panel>

      {spec && (
        <div className={`${s.note} ${s.pg104}`}>
          Fit is measured against your <b className={s.pg14}>{spec.title}</b> spec
          {quality.usable ? " and its job description" : " (title and role only)"}. Tests you send from here use the{" "}
          <b className={s.pg14}>{displayRole(spec.role) || "—"}</b> assessment.{" "}
          <button type="button" className={s.linkTeal} onClick={openEdit}>
            Edit spec
          </button>
        </div>
      )}

      {spec && !quality.usable && (
        <div className={s.flashWarn} style={{ marginTop: 0, marginBottom: 14 }}>
          ⚑ {quality.sentence}, so CVs can only be matched on its title and role, and scores are held at{" "}
          {TITLE_ONLY_CAP}%.{" "}
          <button type="button" className={s.linkTeal} style={{ color: "#e4d5a8" }} onClick={openEdit}>
            Paste the real job description
          </button>{" "}
          — the pool is re-scored against it automatically.
        </div>
      )}

      {!specs.length && !loading && (
        <div className={s.pg110}>
          No job specs yet. Create one, then upload CVs to rank them against it.
        </div>
      )}

      {infoFlash && (
        <div className={`${s.flashOk} ${s.pg117}`}>
          <span className={s.flashOkText}>✓ {infoFlash}</span>
          <button type="button" className={`${s.linkTeal} ${s.pg119}`} onClick={() => setInfoFlash(null)}>
            ✕
          </button>
        </div>
      )}

      {loading && <Spinner className="my-10" />}

      {!loading && loadError && <div className={s.flashWarn}>⚑ {loadError}</div>}

      {specId && !loading && !loadError && (
        <Panel flush>
          <div className={s.pg107}>
            <div className={s.segment}>
              {[
                ["pool", "Ranked pool"],
                ["shortlist", `Shortlist · ${shortCount}`],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={`${s.segmentBtn} ${tab === id ? s.segmentBtnActive : ""}`}
                  onClick={() => setTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === "pool" ? (
              <div className={s.pg108}>
                {pendingCount > 0 && (
                  <span className={s.statCellLabel} style={{ marginRight: 8, color: "#fbbf24" }}>
                    Scoring {pendingCount}…
                  </span>
                )}
                <span className={`${s.statCellLabel} ${s.pg109}`}>Sort</span>
                {chip("fit", "Fit %")}
                {chip("cv", "CV & skills")}
                {chip("exp", "Experience")}
              </div>
            ) : (
              <div className={s.pg38}>
                <button
                  type="button"
                  className={s.btnGhost}
                  style={{ opacity: shortCount ? 1 : 0.5 }}
                  onClick={exportShortlist}
                  disabled={!shortCount}
                >
                  Export CSV
                </button>
                <button
                  type="button"
                  className={s.btnSolid}
                  style={{ opacity: shortWithEmail && spec?.role ? 1 : 0.5, padding: "7px 14px", fontSize: 11 }}
                  onClick={() => sendTests(shortRanked)}
                  disabled={!shortWithEmail || busy || !spec?.role}
                  title={
                    !spec?.role
                      ? "Set an assessment role on this spec before sending tests"
                      : !shortWithEmail
                        ? "None of the shortlisted candidates has an email address"
                        : undefined
                  }
                >
                  {busy ? "Sending…" : "Send psychometric tests →"}
                </button>
              </div>
            )}
          </div>

          {rows.length === 0 ? (
            <div className={s.pg110}>
              {tab === "shortlist" ? (
                <>
                  No shortlisted candidates yet.
                  <br />
                  Tap the ☆ next to anyone in the pool — or open a candidate and "Add to
                  shortlist" — to gather them here, then export or send psychometric tests.
                </>
              ) : (
                <>
                  No candidates in this pool yet.
                  <br />
                  Upload CVs above to start ranking against this spec.
                </>
              )}
            </div>
          ) : (
            <div className={s.pg36}>
              <div className={s.pg111}>
                {rows.map((c, i) => {
                  const scored = isScored(c);
                  const test = TEST_TAGS[c.tag] || TEST_TAGS.cv_only;
                  return (
                    <div
                      key={c.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: ROW_COLS,
                        gap: 11,
                        alignItems: "center",
                        padding: "12px 18px",
                        borderBottom: i < rows.length - 1 ? "1px solid #141e30" : "none",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 800,
                          color: i === 0 && scored ? "#2dd4bf" : "#4a5a7a",
                          textAlign: "center",
                        }}
                      >
                        {scored ? i + 1 : "·"}
                      </div>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          background: AV_GRAD[i % AV_GRAD.length],
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {initialsOf(c.name)}
                      </div>
                      <div className={s.pg112}>
                        <div className={s.pg113}>
                          {c.name}
                          <Tag
                            tone={c.source === "cv" ? "teal" : "muted"}
                            title={c.source === "cv" ? "Scored from the uploaded CV" : "From a typed list — no CV uploaded"}
                          >
                            {c.source === "cv" ? "CV" : "List"}
                          </Tag>
                          <Tag tone={test.tone}>{test.label}</Tag>
                        </div>
                        <div className={s.pg114}>
                          {metaLine(c)}
                          {!c.email && (
                            <span style={{ color: "#d9a54a" }}>
                              {metaLine(c) ? " · " : ""}No email — can't be sent a test
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={`${s.trackThin} ${s.pg115}`}>
                        {scored ? (
                          <div style={{ width: `${c.fit_score}%`, height: "100%", background: fitColor(c.fit_score) }} />
                        ) : (
                          <div style={{ width: "100%", height: "100%", background: "#16223a", opacity: 0.6 }} />
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: scored ? 15 : 10.5,
                          fontWeight: 800,
                          color: scored ? fitColor(c.fit_score) : c.scoring_status === "failed" ? "#f87171" : "#8898b5",
                          textAlign: "right",
                          whiteSpace: "nowrap",
                        }}
                        title={c.scoring_status === "failed" ? c.scoring_error || "Scoring failed" : undefined}
                      >
                        {scored ? `${c.fit_score}%` : c.scoring_status === "failed" ? "Failed" : "Scoring…"}
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleShort(c.id)}
                        title={c.shortlisted ? "Shortlisted" : "Add to shortlist"}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 16,
                          color: c.shortlisted ? "#fbbf24" : "#4a5a7a",
                          lineHeight: 1,
                        }}
                      >
                        {c.shortlisted ? "★" : "☆"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmRemove(false);
                          setDetailId(c.id);
                        }}
                        className={`${s.btnGhost} ${s.pg65}`}
                      >
                        Why? →
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Panel>
      )}

      {specId && (
        <div className={`${s.note} ${s.pg116}`}>
          Fit blends <b className={s.pg14}>CV &amp; skills match 55%</b> and{" "}
          <b className={s.pg14}>experience relevance 45%</b>, both judged against this spec's job
          description — change the spec and the pool is re-scored. Uploaded CVs are read in full;
          list entries have no CV, so they're scored only on the details you typed and rank lower.
          Fit never includes a psychometric score — candidates you send a test to get their result
          in <b className={s.pg14}>Test Sessions</b>.
        </div>
      )}

      {sendFlash && (
        <div className={`${sendFlash.n ? s.flashOk : s.flashWarn} ${s.pg117}`}>
          <span className={sendFlash.n ? s.flashOkText : undefined}>
            {sendFlash.n ? "✓" : "⚑"} {sendFlash.n} psychometric {sendFlash.n === 1 ? "test" : "tests"} queued in
            Test Sessions
            {sendFlash.failed.length ? ` · ${sendFlash.failed.length} not sent: ` : "."}
            {sendFlash.failed
              .slice(0, 3)
              .map((f) => `${f.name} (${f.reason})`)
              .join("; ")}
            {sendFlash.failed.length > 3 ? `; +${sendFlash.failed.length - 3} more` : ""}
          </span>
          <div className={s.pg38}>
            {sendFlash.n > 0 && (
              <button
                type="button"
                className={`${s.btnGhost} ${s.pg118}`}
                onClick={() => navigate("/partner/sessions")}
              >
                View in Test Sessions →
              </button>
            )}
            <button type="button" className={`${s.linkTeal} ${s.pg119}`} onClick={() => setSendFlash(null)}>
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Upload: CV files, or a candidate list */}
      <Modal open={!!uploadModal} onClose={() => !uploadModal?.uploading && setUploadModal(null)} maxWidth={560}>
        {uploadModal && (
          <>
            <div className={s.modalHead}>
              {!uploadModal.uploading && <ModalClose onClose={() => setUploadModal(null)} />}
              <div className={s.modalTitle}>Upload CVs</div>
              <div className={s.modalSub}>
                Each CV is read and scored against your <b className={s.pg13}>{spec?.title}</b> spec.
              </div>
            </div>
            <div className={s.modalBody} style={{ paddingBottom: 22 }}>
              <div className={s.segment} style={{ marginBottom: 16 }}>
                {[
                  ["cv", "CV files"],
                  ["list", "Import a list"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    disabled={uploadModal.uploading}
                    className={`${s.segmentBtn} ${uploadModal.mode === id ? s.segmentBtnActive : ""}`}
                    onClick={() => setUploadModal({ ...uploadModal, mode: id, result: null })}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {uploadModal.mode === "cv" ? (
                <>
                  {uploadModal.result ? (
                    <div style={{ marginBottom: 16 }}>
                      <div
                        className={uploadModal.result.added ? s.flashOk : s.flashWarn}
                        style={{ marginTop: 0, marginBottom: 10 }}
                      >
                        <span className={uploadModal.result.added ? s.flashOkText : undefined}>
                          {uploadModal.result.added
                            ? `✓ ${uploadModal.result.added} CV${uploadModal.result.added === 1 ? "" : "s"} added — scoring against the spec now. Scores appear in the pool as they finish.`
                            : "⚑ No CVs were added."}
                        </span>
                      </div>
                      {uploadModal.result.failed.length > 0 && (
                        <div className={s.infoBox} style={{ padding: "12px 14px", maxHeight: 160, overflowY: "auto" }}>
                          <div className={s.metaLabel} style={{ marginBottom: 8 }}>
                            {uploadModal.result.failed.length} file
                            {uploadModal.result.failed.length === 1 ? "" : "s"} skipped
                          </div>
                          {uploadModal.result.failed.map((f, idx) => (
                            <div key={`${f.file}-${idx}`} style={{ fontSize: 11.5, color: "#c8d4e8", marginBottom: 4 }}>
                              {f.file} — <span style={{ color: "#8898b5" }}>{f.reason}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <label
                      className={s.pg120}
                      style={{
                        cursor: uploadModal.uploading ? "default" : "pointer",
                        display: "block",
                        borderColor: dragOver ? "#2dd4bf" : undefined,
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        if (!uploadModal.uploading) addFiles(e.dataTransfer.files);
                      }}
                    >
                      <IconFileText size={26} color="#8898b5" style={{ margin: "0 auto" }} />
                      <div className={s.pg121}>
                        Drop CV files here, or <span style={{ color: "#2dd4bf" }}>browse</span>
                      </div>
                      <div className={s.pg122}>
                        PDF or DOCX · up to 5MB each · up to {MAX_CVS} at once · name and email are read from each CV
                      </div>
                      <input
                        type="file"
                        multiple
                        disabled={uploadModal.uploading}
                        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          addFiles(e.target.files);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  )}

                  {uploadModal.files.length > 0 && (
                    <div
                      className={s.infoBox}
                      style={{ padding: "8px 12px", marginBottom: 14, maxHeight: 180, overflowY: "auto" }}
                    >
                      {uploadModal.files.map(({ file, problem }) => (
                        <div
                          key={`${file.name}:${file.size}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                            fontSize: 11.5,
                            padding: "4px 0",
                          }}
                        >
                          <span
                            style={{
                              color: problem ? "#f87171" : "#c8d4e8",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                            title={file.name}
                          >
                            {file.name}{" "}
                            <span style={{ color: problem ? "#f87171" : "#4a5a7a" }}>
                              · {problem || fmtSize(file.size)}
                            </span>
                          </span>
                          {!uploadModal.uploading && (
                            <button
                              type="button"
                              aria-label={`Remove ${file.name}`}
                              onClick={() =>
                                setUploadModal((m) => ({ ...m, files: m.files.filter((f) => f.file !== file) }))
                              }
                              style={{ background: "transparent", border: "none", cursor: "pointer", color: "#4a5a7a", padding: 0 }}
                            >
                              <IconX size={13} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {uploadModal.progress && uploadModal.uploading && (
                    <div style={{ marginBottom: 14 }}>
                      <div className={`${s.trackThin} ${s.pg115}`}>
                        <div
                          style={{
                            width: `${Math.round((uploadModal.progress.done / uploadModal.progress.total) * 100)}%`,
                            height: "100%",
                            background: "linear-gradient(90deg,#2dd4bf,#0f766e)",
                          }}
                        />
                      </div>
                      <div className={s.note} style={{ marginTop: 6 }}>
                        Uploading {uploadModal.progress.done} of {uploadModal.progress.total}…
                      </div>
                    </div>
                  )}

                  {badFiles.length > 0 && !uploadModal.uploading && (
                    <div className={`${s.note} ${s.pg124}`} style={{ color: "#f87171" }}>
                      {badFiles.length} file{badFiles.length === 1 ? "" : "s"} in red will be skipped.
                    </div>
                  )}

                  <div className={s.pg82}>
                    <button
                      type="button"
                      className={s.btnOutline}
                      disabled={uploadModal.uploading}
                      onClick={() => setUploadModal(null)}
                    >
                      {uploadModal.result ? "Done" : "Cancel"}
                    </button>
                    {uploadModal.result ? (
                      <button
                        type="button"
                        className={`${s.btnSolid} ${s.pg97}`}
                        onClick={() => setUploadModal({ ...uploadModal, result: null })}
                      >
                        Upload more CVs
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`${s.btnSolid} ${s.pg97}`}
                        onClick={uploadCvs}
                        disabled={!goodFiles.length || uploadModal.uploading}
                        style={{ opacity: goodFiles.length ? 1 : 0.5 }}
                      >
                        {uploadModal.uploading
                          ? "Uploading…"
                          : goodFiles.length
                            ? `Score ${goodFiles.length} CV${goodFiles.length === 1 ? "" : "s"} against spec →`
                            : "Score against spec →"}
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <label className={s.pg120} style={{ cursor: "pointer", display: "block", padding: 18 }}>
                    <IconFileText size={22} color="#8898b5" style={{ margin: "0 auto" }} />
                    <div className={s.pg121}>Choose a CSV or text file</div>
                    <div className={s.pg122}>
                      or paste below — one candidate per line: name, email, years of experience, qualification
                    </div>
                    <input
                      type="file"
                      accept=".csv,.txt,text/csv,text/plain"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        readListFile(file);
                      }}
                    />
                  </label>

                  <label className={s.label}>
                    One candidate per line — a header row (name, email, years, qualification) is optional
                  </label>
                  <textarea
                    className={`${s.textarea} ${s.pg123}`}
                    value={uploadModal.text}
                    onChange={(e) => setUploadModal({ ...uploadModal, text: e.target.value })}
                    placeholder={
                      "J. Adeyemi, j.adeyemi@example.com, 6 yrs, BSc Economics\nF. Balogun, f.balogun@example.com, 4 yrs, MBA\nK. Obi, k.obi@example.com, 3 yrs, BA Business"
                    }
                  />
                  <div className={`${s.note} ${s.pg124}`}>
                    {listParse?.candidates.length ? (
                      <>
                        <b className={s.pg14}>{listParse.candidates.length}</b> candidate
                        {listParse.candidates.length === 1 ? "" : "s"} found ·{" "}
                        {listParse.candidates.filter((c) => c.email).length} with an email
                        {listParse.skipped.length ? ` · line ${listParse.skipped.join(", ")} skipped (no name)` : ""}
                        .{" "}
                      </>
                    ) : null}
                    A list has no CV, so each person is scored only on the details you type and ranks
                    below uploaded CVs. Include an email so you can send them a test.
                  </div>

                  <div className={s.pg82}>
                    <button type="button" className={s.btnOutline} onClick={() => setUploadModal(null)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`${s.btnSolid} ${s.pg97}`}
                      onClick={importList}
                      disabled={busy || !listParse?.candidates.length}
                      style={{ opacity: listParse?.candidates.length ? 1 : 0.5 }}
                    >
                      {busy ? "Scoring…" : "Score against spec →"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* New / edit spec */}
      <Modal open={!!specModal} onClose={() => setSpecModal(null)} maxWidth={500}>
        {specModal && (
          <>
            <div className={s.modalHead}>
              <ModalClose onClose={() => setSpecModal(null)} />
              <div className={s.modalTitle}>
                {specModal.mode === "new" ? "New job spec" : "Edit job spec"}
              </div>
              <div className={s.modalSub}>
                Every CV in the pool is scored against this title and job description. The
                assessment role is the test candidates receive if you send them one.
              </div>
            </div>
            <div className={s.modalBody} style={{ paddingBottom: 22 }}>
              <div className={s.field}>
                <label className={s.label}>Spec title</label>
                <input
                  className={s.input}
                  value={specModal.title}
                  onChange={(e) => setSpecModal({ ...specModal, title: e.target.value })}
                  placeholder="e.g. Business Analyst — Lagos"
                />
              </div>
              <div className={s.field}>
                <label className={s.label}>Assessment role (for tests you send)</label>
                <select
                  className={s.select}
                  value={displayRole(specModal.role)}
                  onChange={(e) => setSpecModal({ ...specModal, role: e.target.value })}
                >
                  <PartnerRoleOptions roles={roles} preserveValue={specModal.role} />
                </select>
              </div>
              <div className={s.pg6}>
                <label className={s.label}>Job description</label>
                <textarea
                  className={`${s.textarea} ${s.pg125}`}
                  value={specModal.description}
                  onChange={(e) => setSpecModal({ ...specModal, description: e.target.value })}
                  placeholder="Paste the job description — responsibilities, required skills, tools, seniority… CVs are matched against this."
                />
                {String(specModal.description || "").trim() && !assessDescription(specModal.description).usable && (
                  <div className={s.note} style={{ marginTop: 6, color: "#d9a54a" }}>
                    {assessDescription(specModal.description).reason === "placeholder"
                      ? "This looks like placeholder text (lorem ipsum)."
                      : "This is too short to list any requirements."}{" "}
                    CVs would only be matched on the title and role, with scores held at {TITLE_ONLY_CAP}%.
                  </div>
                )}
                {specModal.mode === "edit" && (
                  <div className={s.note} style={{ marginTop: 6 }}>
                    Changing the title, role or description re-scores everyone in this pool.
                  </div>
                )}
              </div>
              <div className={s.pg82}>
                <button type="button" className={s.btnOutline} onClick={() => setSpecModal(null)}>
                  Cancel
                </button>
                <button type="button" className={`${s.btnSolid} ${s.pg97}`} onClick={saveSpec} disabled={busy}>
                  {busy ? "Saving…" : specModal.mode === "new" ? "Create spec →" : "Save changes"}
                </button>
              </div>
              {specModal.mode === "edit" && (
                <div style={{ marginTop: 14, textAlign: "center" }}>
                  <button
                    type="button"
                    className={s.linkTeal}
                    style={{ color: "#f87171" }}
                    onClick={removeSpec}
                    disabled={busy}
                  >
                    {specModal.confirmDelete
                      ? "Click again to delete this spec and hide its pool"
                      : "Delete this spec"}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* Breakdown */}
      <Modal open={!!detail} onClose={() => setDetailId(null)} maxWidth={540}>
        {detail && (
          <>
            <div className={`${s.modalHead} ${s.pg83}`}>
              <ModalClose onClose={() => setDetailId(null)} />
              <div className={s.pg23}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: AV_GRAD[Math.max(0, ranked.findIndex((x) => x.id === detail.id)) % AV_GRAD.length],
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  {initialsOf(detail.name)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className={`${s.modalTitle} ${s.modalTitleLg}`}>{detail.name}</div>
                  <div className={s.modalSub}>
                    {[spec?.title, detail.email || "no email on file"].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </div>
              <div className={s.scoreBadge}>
                <div
                  className={s.scoreValue}
                  style={{ color: isScored(detail) ? fitColor(detail.fit_score) : "#8898b5" }}
                >
                  {isScored(detail) ? `${detail.fit_score}%` : "…"}
                </div>
                <div className={s.scoreLabel}>Overall fit</div>
              </div>
            </div>

            <div className={s.modalBody} style={{ paddingBottom: 22 }}>
              {isScored(detail) ? (
                <>
                  <div className={`${s.metaLabel} ${s.pg11}`}>How this score is built</div>
                  {[
                    ["CV & skills match", detail.cv_score, "55%"],
                    ["Experience relevance", detail.experience_score, "45%"],
                  ].map(([label, value, weight]) => (
                    <div className={s.pg11} key={label}>
                      <div className={s.pg126}>
                        <span className={s.pg13}>
                          {label} <span className={s.pg127}>· weight {weight}</span>
                        </span>
                        <span style={{ color: fitColor(value), fontWeight: 700 }}>{value}%</span>
                      </div>
                      <div className={`${s.trackThin} ${s.pg115}`}>
                        <div
                          style={{
                            width: `${value}%`,
                            height: "100%",
                            background: "linear-gradient(90deg,#2dd4bf,#0f766e)",
                          }}
                        />
                      </div>
                    </div>
                  ))}

                  <div className={`${s.metaLabel} ${s.pg86}`}>Why this ranking</div>
                  {detail.spec_basis === "title_only" && (
                    <div className={s.flashWarn} style={{ marginTop: 0, marginBottom: 10 }}>
                      ⚑ Measured against the job title and role only — the spec had no usable job description,
                      so scores are held at {TITLE_ONLY_CAP}%.{" "}
                      <button
                        type="button"
                        className={s.linkTeal}
                        style={{ color: "#e4d5a8" }}
                        onClick={() => {
                          setDetailId(null);
                          openEdit();
                        }}
                      >
                        Add the job description
                      </button>
                    </div>
                  )}
                  <div className={`${s.infoBox} ${s.pg87}`}>
                    <div className={s.pg129}>
                      {detail.fit_summary || "No explanation was recorded for this score."}
                    </div>
                    {detail.matched_skills?.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div className={s.pg127} style={{ marginBottom: 6 }}>
                          Spec requirements met
                        </div>
                        {detail.matched_skills.map((m) => (
                          <Chip key={`m-${m}`}>{m}</Chip>
                        ))}
                      </div>
                    )}
                    {detail.missing_skills?.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <div className={s.pg127} style={{ marginBottom: 6 }}>
                          Spec requirements not found {detail.source === "cv" ? "in the CV" : "in the list details"}
                        </div>
                        {detail.missing_skills.map((m) => (
                          <Chip key={`x-${m}`} muted>
                            {m}
                          </Chip>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={s.note} style={{ marginTop: 8 }}>
                    {detail.source !== "cv"
                      ? "From a typed list — there's no CV, so this is a keyword match on the details given. Upload their CV for a full read."
                      : detail.scoring_engine === "ai"
                        ? `AI review of ${detail.cv_filename || "the CV"} against this spec — check the evidence before deciding.`
                        : `Keyword match of ${detail.cv_filename || "the CV"} against this spec.`}
                  </div>
                </>
              ) : (
                <div className={detail.scoring_status === "failed" ? s.flashWarn : s.infoBox} style={{ padding: "14px 16px" }}>
                  {detail.scoring_status === "failed"
                    ? `⚑ ${detail.scoring_error || "This CV couldn't be scored."}`
                    : "This CV is still being scored against the spec — the breakdown appears here when it's done."}
                </div>
              )}

              {detail.tag !== "cv_only" && (
                <div className={`${s.note} ${s.pg128}`}>
                  {detail.tag === "assessed"
                    ? `Psychometric test result: ${detail.psychometric_score}% — see the full report in Test Sessions. It is not part of the fit score.`
                    : detail.tag === "completed"
                      ? "This candidate has completed the psychometric test — the result is in Test Sessions. It is not part of the fit score."
                      : detail.tag === "expired"
                        ? "The psychometric test sent to this candidate expired before they took it."
                        : "A psychometric test has been sent to this candidate — the result will appear in Test Sessions."}
                </div>
              )}

              <div className={s.modalActions} style={{ padding: 0, marginTop: 18 }}>
                <button
                  type="button"
                  className={`${detail.shortlisted ? s.btnMuted : s.btnSolid} ${s.pg97}`}
                  onClick={() => toggleShort(detail.id)}
                >
                  {detail.shortlisted ? "✓ Shortlisted" : "Add to shortlist"}
                </button>
                <button
                  type="button"
                  className={s.btnOutline}
                  disabled={busy || !detail.email || !spec?.role || detail.tag === "sent"}
                  title={
                    !detail.email
                      ? "No email on file for this candidate"
                      : !spec?.role
                        ? "Set an assessment role on this spec first"
                        : detail.tag === "sent"
                          ? "A test is already out to this candidate"
                          : undefined
                  }
                  style={{ opacity: detail.email && spec?.role && detail.tag !== "sent" ? 1 : 0.5 }}
                  onClick={() => {
                    sendTests([detail]);
                    setDetailId(null);
                  }}
                >
                  {detail.tag === "cv_only" ? "Send assessment" : detail.tag === "sent" ? "Test sent" : "Send again"}
                </button>
              </div>
              <div style={{ marginTop: 14, textAlign: "center" }}>
                <button
                  type="button"
                  className={s.linkTeal}
                  style={{ color: "#f87171" }}
                  disabled={busy}
                  onClick={() => (confirmRemove ? removeCandidate(detail.id) : setConfirmRemove(true))}
                >
                  {confirmRemove ? "Click again to remove from this pool" : "Remove from pool"}
                </button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
